import Moment from 'moment'
import moment from 'moment-timezone'

Moment.updateLocale('ja', {})
moment.tz.setDefault('Asia/Tokyo')

const defaultFormat = 'YYYY/MM/DD'

export const date = (): moment.Moment => moment()

export const endOfDay = ():Date => moment().hour(23).minute(59).second(59)
  .toDate()

export const maxDate = ():Date => moment().year(2037).month(12).day(31)
  .hour(23)
  .minute(59)
  .second(59)
  .toDate()

export const nowDate = (customFormat = defaultFormat):string => moment().format(customFormat)
export const nowEpochMS = ():number => moment().valueOf()

export const yesterDay = (customFormat = defaultFormat):string => moment().subtract(1, "days").format(customFormat)

export const sevenDaysAgo = (customFormat = defaultFormat):string => moment().subtract(1, "weeks").format(customFormat)

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const dateManipulateDay = (dateVal: any, num: number, format = defaultFormat):string => moment(dateVal).add(num, 'day').format(format)

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const dateManipulateMonth = (dateVal: any, num: number, format = defaultFormat):string => moment(dateVal).add(num, 'month').format(format)

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const format = (dateVal: any, formatVal = defaultFormat, lang?: string):string => {
  if (lang) {
    moment.locale(lang)
  }
  return moment(dateVal).format(formatVal)
}

export const combineYearMonthAndDay = (yeaMonth: string, day: number, formatVal = defaultFormat, lang?: string):string => {
  if (lang) {
    moment.locale(lang)
  }
  const paddingDay = `00${day}`.slice(-2)
  return moment(`${yeaMonth}${paddingDay}`).format(formatVal)
}

export const combineYearAndMonth = (year: number, month: number):string => {
  const paddingMonth = `00${month}`.slice(-2)
  return `${year}${paddingMonth}`
}

export const isCheckFormat = (dateVal: string, formatVal = defaultFormat):boolean => moment(dateVal, formatVal, true).isValid()

export const isSameMonth = (startMonthDate: string, targetDate: string):boolean => moment(startMonthDate).isSame(targetDate, 'month')

export const getYear = (dateVal?: string):number => moment(dateVal).year()

export const getMonth = (dateVal?: string):number => moment(dateVal).month() + 1

// eslint-disable-next-line arrow-body-style
export const diffDays = (beforeDateVal: string | Date, afterDateVal: string | Date):number => {
  return Moment(afterDateVal, defaultFormat).diff(beforeDateVal, 'day')
}

// eslint-disable-next-line arrow-body-style
export const diffMonth = (beforeDateVal: string | Date, afterDateVal: string | Date):number => {
  return Moment(afterDateVal, defaultFormat).diff(beforeDateVal, 'month')
}

export const diffNowToDate = (dateVal: string | Date):number => Moment(dateVal, defaultFormat).diff(nowDate(), 'day')
export const diffNowToMillSeconds = (millSeconds: number):number => nowEpochMS() - millSeconds

export const toDate = (dateVal: string | Date | undefined):Date => moment(dateVal).toDate()

export const convertSecondsToHourAndMinute = (seconds: number):string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = `00${Math.floor((seconds % 3600) / 60)}`.slice(-2)
  return `${hours}:${minutes}`
}

export const attendanceTimeFormat = (dateVal: string):string => {
  const tokyo_time = moment(dateVal).tz('Asia/Tokyo')
  const minutes = tokyo_time.format('mm')
  const hours = Number(tokyo_time.format('k')) > 4 ? tokyo_time.format('kk') : Number(tokyo_time.format('k')) + 24
  return `${hours}:${minutes}`
}

export const attendanceTimeToStandard = (value: string):string => {
  const [h, m] = value.split(":").map(Number)
  return `${(`00${h % 24}`).slice(-2)}:${(`00${m}`).slice(-2)}`
}

export const standardToAttendanceTime = (hour: number, minute: number): string => {
  const displayHour = hour < 5 ? hour + 24 : hour
  return `${displayHour}:${(`00${minute}`).slice(-2)}`
}

// eslint-disable-next-line arrow-body-style
export const correctionDate = (dateVal: string, formatVal = defaultFormat):string => {
  return moment(dateVal + moment().format(' HH:mm:ss')).subtract(6, "hours").format(formatVal).toString()
}

export const subtractMonth = (dateVal: string, prev_num: number):string => moment(dateVal).subtract(prev_num, 'months').toString()

export const startOfWeek = (dateVal: string):string => moment(dateVal).startOf('weeks').toString()

export const endOfWeek = (dateVal: string):string => moment(dateVal).endOf('weeks').toString()

export const startOfLastWeek = (dateVal: string):string => moment(dateVal).subtract(1, 'weeks').startOf('weeks').toString()

export const endOfLastWeek = (dateVal: string):string => moment(dateVal).subtract(1, 'weeks').endOf('weeks').toString()

export const startOfNextWeek = (dateVal: string):string => moment(dateVal).add(1, 'weeks').startOf('weeks').toString()

export const endOfNextWeek = (dateVal: string):string => moment(dateVal).add(1, 'weeks').endOf('weeks').toString()

export const startOfMonth = (dateVal: string, formatVal = defaultFormat):string => moment(dateVal).startOf('months').format(formatVal).toString()

export const endOfMonth = (dateVal: string, formatVal = defaultFormat):string => moment(dateVal).endOf('months').format(formatVal).toString()

// eslint-disable-next-line arrow-body-style
export const prevMonth = (dateVal: string, formatVal = defaultFormat):string => {
  return moment(dateVal).subtract(1, 'months').format(formatVal).toString()
}

// eslint-disable-next-line arrow-body-style
export const nextMonth = (dateVal: string, formatVal = defaultFormat):string => {
  return moment(dateVal).add(1, 'months').format(formatVal).toString()
}

export const weekList = (dateVal: string, formatVal = defaultFormat):string[] => {
  const startOfWeekNum = Number(moment(dateVal).startOf('months').format('w'))
  const endOfWeekNum = Number(moment(dateVal).endOf('months').format('w'))
  let weekNumRange: any[] = []
  if (startOfWeekNum > endOfWeekNum) {
    const prevWeekNum = moment(dateVal).subtract(1, 'years').weeksInYear()
    weekNumRange = Array((prevWeekNum - startOfWeekNum) + 1).fill(null).map((_, i) => i)
  } else if (startOfWeekNum < endOfWeekNum) {
    weekNumRange = Array((endOfWeekNum - startOfWeekNum) + 1).fill(null).map((_, i) => i)
  }
  if (weekNumRange.length) {
    return weekNumRange.map((num) => moment(dateVal).add(num, 'weeks').startOf('weeks').format(formatVal))
  }
  return []
}

export const toHourMinute = (sec: number): {hour: number, minute: string} => ({
  hour: Math.floor(sec / 3600),
  minute: `00${Math.floor((sec % 3600) / 60)}`.slice(-2),
})

export const secToHour = (sec: number): number => (
  Math.floor(sec / 3600)
)

export const secToTwoDigitsHour = (sec: number): string => (
  `00${Math.floor(sec / 3600)}`.slice(-2)
)

export const secToMinute = (sec: number, move_up = true): string => {
  if (move_up) {
    return `${Math.floor((sec % 3600) / 60)}`
  }
  return `${Math.floor(sec / 60)}`
}

export const secToTwoDigitsMinute = (sec: number): string => (
  `00${Math.floor((sec % 3600) / 60)}`.slice(-2)
)

export const secToSec = (sec: number): string => (
  `${Math.floor((sec % 3600) % 60)}`
)

export const secToTwoDigitsSec = (sec: number): string => (
  `00${Math.floor((sec % 3600) % 60)}`.slice(-2)
)

export const buildWorkTime = (sec: number): string => {
  const hours = `00${Math.floor(sec / 3600)}`.slice(-2)
  const minutes = `00${Math.floor((sec % 3600) / 60)}`.slice(-2)
  const seconds = `00${Math.floor((sec % 3600) % 60)}`.slice(-2)
  return `${hours}:${minutes}:${seconds}`
}

export const secToHourMinute = (sec: number): string => {
  const hours = secToHour(sec)
  const minutes = `00${secToMinute(sec)}`.slice(-2)
  return `${hours}:${minutes}`
}

export const secToTwoDigitsHourMinute = (sec: number): string => {
  const hours = `00${secToHour(sec)}`.slice(-2)
  const minutes = `00${secToMinute(sec)}`.slice(-2)
  return `${hours}:${minutes}`
}

export const digitsHourMinuteToNumberHourMinute = (timeStr: string): { hour: number, minute: number } => {
  const tmp = timeStr.split(":")
  const hour = Number(tmp[0])
  const minute = Number(tmp[1])
  return { hour: hour, minute: minute }
}

export const dateToTodayElapsedSeconds = (dateTime: string, beforeDateTime?: string): number => {
  const dateObj = moment(dateTime).tz('Asia/Tokyo')
  if (beforeDateTime) {
    return (Number(dateObj.format("H")) * 3600) + (Number(dateObj.format("m")) * 60) + Number(dateObj.format("s"))
    + (diffDays(format(beforeDateTime), format(dateObj)) * 86400)
  }
  return (Number(dateObj.format("H")) * 3600) + (Number(dateObj.format("m")) * 60) + Number(dateObj.format("s"))
}

export const minutesAdjustor = (dateTime: string, minute: number, ceil: boolean): string => {
  const dateObj = moment(dateTime).tz('Asia/Tokyo')
  const adjustMinutes = Number(dateObj.format("s")) > 0 && ceil ? Number(dateObj.format("m")) + 1 : Number(dateObj.format("m"))
  if (ceil) {
    dateObj.add((Math.ceil(adjustMinutes / minute)) * minute, "m")
  } else {
    dateObj.add((Math.floor(adjustMinutes / minute)) * minute, "m")
  }
  dateObj.second(0)
  return dateObj.toString()
}

export const secondsAdjustor = (dateTime: string, seconds: number, isAdd: boolean): string => {
  if (isAdd) {
    return moment(dateTime).add(seconds, "seconds").toString()
  }
  return moment(dateTime).subtract(seconds, "seconds").toString()
}

export const isToday = (dateTime: string): boolean => new Date().toDateString() === new Date(dateTime).toDateString()

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const addMinutes = (dateVal: any, num: number, formatVal = defaultFormat):string => moment(dateVal).add(num, 'minutes').format(formatVal)

export const minDateTime = (dateTimeList: string[]): string => (
  moment.min(dateTimeList.map((v) => moment(v))).toString()
)

export const maxDateTime = (dateTimeList: string[]): string => (
  moment.max(dateTimeList.map((v) => moment(v))).toString()
)
